import type { SitGoOutcome, SitGoTemplate } from 'reducers/sitGoResult';
import { isValidTournamentId } from 'utils/tournamentSubscriptions';

/**
 * EN-11109: pure decisions behind the Sit & Go result offer, kept apart from
 * the watcher so they can be pinned without a store or a socket.
 */

export type SitGoResultDetection = {
  gameID: string;
  tournamentId: string;
  game: string;
  outcome: SitGoOutcome;
  tableFinished: boolean;
  /**
   * The row can be trusted to say won or lost (EN-11139). A bust at tStatus
   * 'P' gets its position BEFORE the payout pass writes its prize and
   * `claimed`, so a paid bust read early says "lost". Until `claimed` (or a
   * prize) lands the watcher holds it for SIT_GO_BUST_SETTLE_MS — not
   * indefinitely, because an unpaid early bust is only marked claimed once
   * someone else busts into the money, minutes later.
   */
  settled: boolean;
};

/** How long an unclaimed bust row is held before it is shown as it stands. */
export const SIT_GO_BUST_SETTLE_MS = 2_000;

// The lobby broadcast formats some counts with thousands separators
// (startingChips "3,000"), which Number() reads as NaN.
const num = (value: unknown): number => {
  const n = Number(typeof value === 'string' ? value.replace(/,/g, '') : value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The Sit & Go results this player has reached on their open tables.
 *
 * A player's Sit & Go ends for them at their own bust (their gotTnmStatus row
 * reads status 'F', placed, while the tournament may still be 'P' for the
 * others) or
 * when the tournament itself finishes (tStatus 'F'). Only Sit & Go tables
 * (tabletype 't') are considered: an MTT never starts an offer. A cancelled
 * tournament ('C') keeps its own refund notice.
 *
 * `rows` is gotTnmStatus (state.currentTable.tournamentStatus): one row per
 * player per tournament, the backend's tournament_stats joined to tournaments.
 */
export const detectSitGoResults = (
  tables: Record<string, any> | undefined,
  rows: unknown,
  playerGUID: string
): SitGoResultDetection[] => {
  if (!playerGUID || !tables || !Array.isArray(rows)) return [];
  const results: SitGoResultDetection[] = [];
  for (const [gameID, table] of Object.entries(tables)) {
    const game = table?.game;
    if (game?.tabletype !== 't') continue;
    const tournamentId = String(game?.tournamentId ?? '');
    if (!tournamentId || tournamentId === '0') continue;
    const row: any = rows.find(
      (r: any) => r?.playerId === playerGUID && String(r?.tournamentId) === tournamentId
    );
    if (!row || row.tStatus === 'C') continue;
    const tournamentFinished = row.tStatus === 'F';
    // A bust counts once the backend has placed it below the winner. Position
    // 0 is its "Game is processing..." state, and position 1 at tStatus 'P' is
    // the winner being finalized, whose payout lands before the tournament
    // flips to 'F' — starting there would show the winner "Sorry, you lost".
    const playerBusted =
      (row.status ?? row.pStatus) === 'F' && row.tStatus === 'P' && num(row.position) > 1;
    if (!tournamentFinished && !playerBusted) continue;
    const capacity = num(row.maxSeat) || num(game?.maxSeat);
    results.push({
      gameID: String(gameID),
      tournamentId,
      game: String(game?.game || 'texas'),
      outcome: {
        position: num(row.position) || null,
        prize: num(row.prize),
        capacity,
        title: String(row.title || game?.title || '')
      },
      // Heads-up: one player's bust IS the end of the table.
      tableFinished: tournamentFinished || capacity === 2,
      settled: tournamentFinished || num(row.claimed) === 1 || num(row.prize) > 0
    });
  }
  return results;
};

/**
 * The tournaments of the player's open Sit & Go tables, sorted so the joined
 * list is a stable key: the feeds SitGoResultWatcher holds (see there).
 */
export const openSitGoTournamentIds = (tables: Record<string, any> | undefined): string[] => {
  const ids = new Set<string>();
  Object.values(tables || {}).forEach((table: any) => {
    if (table?.game?.tabletype !== 't') return;
    const tournamentId = table?.game?.tournamentId;
    if (isValidTournamentId(tournamentId)) ids.add(String(tournamentId));
  });
  return Array.from(ids).sort();
};

/**
 * The lobby row of a Sit & Go, as the rematch's template key. The engine
 * spawns the next table of a template by matching these fields
 * (shared/repositories/sql/tournaments/queries.js), so a later 'R' row with
 * the same values IS the next table of the same template.
 */
export const toSitGoTemplate = (row: any): SitGoTemplate | null => {
  const tournamentId = String(row?.tournamentId ?? row?.id ?? '');
  if (!tournamentId) return null;
  return {
    tournamentId,
    title: String(row?.tablename ?? row?.title ?? ''),
    game: String(row?.game ?? ''),
    buyIn: num(row?.buyIn),
    entryFee: num(row?.entryFee),
    minPlayers: num(row?.minPlayers),
    maxPlayers: num(row?.maxPlayers),
    startingChips: num(row?.startingChips),
    schedule: String(row?.schedule ?? ''),
    maxSeat: num(row?.maxSeat)
  };
};

const sameTemplate = (a: SitGoTemplate, b: SitGoTemplate): boolean =>
  a.title === b.title &&
  a.game === b.game &&
  a.buyIn === b.buyIn &&
  a.entryFee === b.entryFee &&
  a.minPlayers === b.minPlayers &&
  a.maxPlayers === b.maxPlayers &&
  a.startingChips === b.startingChips &&
  a.schedule === b.schedule;

// Registered and capacity as the lobby card reads them (adaptSitGo).
const registeredCount = (row: any): number =>
  num(row?.seatsTaken ?? row?.registeredPlayers ?? row?.registered ?? row?.playerCount);
const seatCapacity = (row: any): number => num(row?.capacity ?? row?.maxPlayers);
const rowId = (row: any): number => {
  const id = Number(row?.tournamentId ?? row?.id);
  return Number.isFinite(id) ? id : Number.POSITIVE_INFINITY;
};
const lowestId = (rows: any[]): any | null =>
  rows.reduce<any | null>(
    (best, row) => (best === null || rowId(row) < rowId(best) ? row : best),
    null
  );

/**
 * The next table of the same template: a registering ('R') Sit & Go with the
 * same key and a different tournamentId — the one the lobby shows
 * (orderSitGoSection): the lowest-id part-filled table, else the lowest-id
 * empty one. The server deletes every other empty table of the template
 * (EN-11139), so the rematch must not land on one of those. A full table that
 * has not started is never offered. Null when the lobby lists no such table.
 */
export const findRematchTournament = (
  template: SitGoTemplate | null | undefined,
  sitAndGoList: unknown
): any | null => {
  if (!template || !Array.isArray(sitAndGoList)) return null;
  const sameTemplateRows = sitAndGoList.filter((row: any) => {
    if (row?.status !== 'R') return false;
    const candidate = toSitGoTemplate(row);
    return (
      !!candidate &&
      candidate.tournamentId !== template.tournamentId &&
      sameTemplate(candidate, template)
    );
  });
  const isFull = (row: any) => {
    const capacity = seatCapacity(row);
    return capacity > 0 && registeredCount(row) >= capacity;
  };
  const partFilled = sameTemplateRows.filter((row) => registeredCount(row) > 0 && !isFull(row));
  const empty = sameTemplateRows.filter((row) => registeredCount(row) === 0);
  return lowestId(partFilled) ?? lowestId(empty);
};

/** The lobby seat toggle that shows the format just played. */
export const sitgoSeatsForCapacity = (capacity: number): 'headsUp' | 'sixSeat' =>
  capacity === 2 ? 'headsUp' : 'sixSeat';
